import { Stickers, type BrowseEntry, type StickerCreatorState, type StickerCreatorViewModel, type StickersState, type StickersViewModel } from "@bundle/core";
import type { GifContentFilter, StickerPack } from "@bundle/contracts";
import { useState } from "react";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { useStickers } from "../context/StickersContext.tsx";
import { SettingsSection } from "./settings/SettingsKit.tsx";
import {
  Button,
  DropZone,
  FilePicker,
  Icon,
  IconButton,
  Image,
  ImageCropper,
  MediaGrid,
  Menu,
  Pressable,
  Sheet,
  Spacer,
  Stack,
  Text,
  TextField,
  Toast,
  Toggle,
  type MenuItem,
} from "../primitives/index.ts";

/** Pack icon shown in rows. */
function PackThumb({ pack }: { pack: StickerPack }) {
  const icon = Stickers.packIcon(pack);
  if (icon === undefined) return <Stack width="avatar.lg" height="avatar.lg" radius="md" background="bg.hover" />;
  return <Image src={icon.url} alt={pack.name} width={40} height={40} fit="contain" />;
}

// ---------- Create sticker ----------

export type StickerCreatorSheetProps = { vm: StickerCreatorViewModel; state: StickerCreatorState; testID: string };

/** Pick/paste an image → crop to a square (static images) → name, keywords and pack → save. */
export function StickerCreatorSheet({ vm, state: s, testID }: StickerCreatorSheetProps) {
  const t = useT();
  const tr = useTr();
  const [choosing, setChoosing] = useState(false);
  const pick = (files: { name: string; mime: string; size: number; lastModified: number; source: unknown }[]) => {
    const f = files.find((x) => x.mime.startsWith("image/")) ?? files[0];
    if (f !== undefined) void vm.pickFile(f);
  };
  const newPack = s.choice.kind === "new" ? s.choice : undefined;
  return (
    <Sheet open={s.open} onClose={() => vm.close()} title={t("stickers.create.title")} closeLabel={t("common.close")} testID={testID}>
      <FilePicker open={choosing} accept="image/png,image/jpeg,image/webp,image/gif,image/apng" onPick={pick} onClose={() => setChoosing(false)} testID={`${testID}-file`} />
      {!s.allowed ? (
        <Text color="text.secondary" testID={`${testID}-not-allowed`}>
          {t("stickers.create.notAllowed")}
        </Text>
      ) : s.step === "pick" ? (
        <DropZone onFiles={pick} label={t("stickers.create.pick")} testID={`${testID}-drop`}>
          <Stack gap="3" align="center" padding="5" radius="lg" border="border.subtle">
            {s.inspecting ? (
              <Text color="text.secondary">{t("stickers.create.reading")}</Text>
            ) : (
              <>
                <Button label={t("stickers.create.pick")} icon="image" variant="primary" onPress={() => setChoosing(true)} testID={`${testID}-choose`} />
                <Text variant="footnote" color="text.secondary" align="center">
                  {t("stickers.create.pickHint")}
                </Text>
                <Stack align="stretch">
                  <TextField value="" onChange={() => {}} placeholder={t("stickers.create.pastePlaceholder")} onPasteFiles={pick} testID={`${testID}-paste`} />
                </Stack>
              </>
            )}
            {s.error === undefined ? null : (
              <Text variant="footnote" color="status.danger" testID={`${testID}-error`}>
                {tr(s.error)}
              </Text>
            )}
          </Stack>
        </DropZone>
      ) : (
        <Stack gap="3">
          {s.previewUrl === undefined ? null : s.animated ? (
            <Stack align="center" gap="1">
              <Image src={s.previewUrl} alt={s.name} width={200} height={200} fit="contain" />
              <Text variant="caption" color="text.secondary">
                {t("stickers.create.animatedNote")}
              </Text>
            </Stack>
          ) : (
            <Stack gap="1">
              <ImageCropper src={s.previewUrl} aspect={1} onCropChange={(r) => vm.setCrop(r)} label={t("stickers.create.crop")} testID={`${testID}-cropper`} />
              <Text variant="caption" color="text.secondary">
                {t("stickers.create.crop")}
              </Text>
            </Stack>
          )}
          <Stack direction="horizontal" align="center">
            <Button label={t("stickers.create.change")} variant="ghost" size="sm" icon="image" onPress={() => setChoosing(true)} testID={`${testID}-change`} />
          </Stack>
          {s.canRemoveBackground && !s.animated ? (
            <Toggle value={s.removeBackground} onChange={(v) => vm.setRemoveBackground(v)} label={t("stickers.create.removeBackground")} testID={`${testID}-remove-bg`} />
          ) : null}
          <TextField label={t("stickers.create.name")} value={s.name} onChange={(v) => vm.setName(v)} placeholder={t("stickers.create.namePlaceholder")} testID={`${testID}-name`} />
          <TextField
            label={t("stickers.create.keywords")}
            value={s.keywords}
            onChange={(v) => vm.setKeywords(v)}
            placeholder={t("stickers.create.keywordsPlaceholder")}
            testID={`${testID}-keywords`}
          />
          <Stack gap="1">
            <Text variant="footnote" weight="semibold" color="text.secondary">
              {t("stickers.create.pack")}
            </Text>
            {s.packs.map((p) => (
              <Pressable
                key={p.id}
                direction="horizontal"
                radius="md"
                selected={s.choice.kind === "existing" && s.choice.packId === p.id}
                onPress={() => vm.choosePack(p.id)}
                testID={`${testID}-pack-${p.id}`}
              >
                <Stack direction="horizontal" align="center" gap="2" padding="1.5">
                  <PackThumb pack={p} />
                  <Text lines={1}>{p.name}</Text>
                </Stack>
              </Pressable>
            ))}
            <Pressable direction="horizontal" radius="md" selected={newPack !== undefined} onPress={() => vm.chooseNewPack()} testID={`${testID}-pack-new`}>
              <Stack direction="horizontal" align="center" gap="2" padding="1.5">
                <Stack width="avatar.lg" height="avatar.lg" radius="md" background="bg.hover" align="center" justify="center">
                  <Icon name="plus" color="text.secondary" />
                </Stack>
                <Text>{t("stickers.create.newPack")}</Text>
              </Stack>
            </Pressable>
            {newPack === undefined ? null : (
              <Stack gap="2" paddingX="1">
                <TextField
                  label={t("stickers.create.newPackName")}
                  value={newPack.name}
                  onChange={(v) => vm.setNewPackName(v)}
                  testID={`${testID}-new-pack-name`}
                />
                {s.canCreateWorkspacePack ? (
                  <Toggle
                    value={newPack.scope === "workspace"}
                    onChange={(v) => vm.setNewPackScope(v ? "workspace" : "personal")}
                    label={t("stickers.create.newPackWorkspace")}
                    testID={`${testID}-new-pack-workspace`}
                  />
                ) : null}
              </Stack>
            )}
          </Stack>
          {s.error === undefined ? null : (
            <Text variant="footnote" color="status.danger" testID={`${testID}-error`}>
              {tr(s.error)}
            </Text>
          )}
          <Stack direction="horizontal" justify="end" gap="2">
            <Button label={t("common.cancel")} variant="ghost" onPress={() => vm.close()} testID={`${testID}-cancel`} />
            <Button
              label={s.step === "saving" ? t("stickers.create.saving") : t("stickers.create.save")}
              variant="primary"
              loading={s.step === "saving"}
              disabled={!s.canSave}
              onPress={() => void vm.save()}
              testID={`${testID}-save`}
            />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}

// ---------- One pack ----------

export type StickerPackSheetProps = {
  vm: StickersViewModel;
  state: StickersState;
  /** "Add a sticker" to this pack (absent: can't create). */
  onAddSticker?: (packId: string) => void;
  testID: string;
};

/** A pack opened from a sticker in a message (or the manager): its stickers, add/remove, and curation. */
export function StickerPackSheet({ vm, state, onAddSticker, testID }: StickerPackSheetProps) {
  const t = useT();
  const v = state.viewing;
  const pack = v?.pack;
  const owner = pack === undefined ? undefined : vm.ws.state.cache?.members[pack.createdBy];
  const items: MenuItem[] = [];
  if (pack !== undefined && v !== undefined) {
    if (v.canChangeScope) {
      items.push(
        pack.scope === "workspace"
          ? { id: "personal", label: t("stickers.pack.makePersonal"), icon: "user", onSelect: () => void vm.setPackScope(pack.id, "personal") }
          : { id: "workspace", label: t("stickers.pack.makeWorkspace"), icon: "users", onSelect: () => void vm.setPackScope(pack.id, "workspace") },
      );
    }
    if (v.canDelete) items.push({ id: "delete", label: t("stickers.pack.delete"), icon: "trash", destructive: true, onSelect: () => void vm.deletePack(pack.id) });
  }
  return (
    <Sheet
      open={v !== undefined}
      onClose={() => vm.closePack()}
      title={pack?.name ?? t("stickers.manage.title")}
      closeLabel={t("common.close")}
      headerActions={
        items.length === 0 ? undefined : (
          <Menu items={items} align="end" testID={`${testID}-menu`}>
            <IconButton icon="more" size="sm" accessibilityLabel={t("message.more")} onPress={() => {}} testID={`${testID}-more`} />
          </Menu>
        )
      }
      testID={testID}
    >
      {v === undefined ? null : v.status === "loading" && pack === undefined ? (
        <Text color="text.secondary">{t("common.loading")}</Text>
      ) : pack === undefined ? (
        <Text color="text.secondary" testID={`${testID}-gone`}>
          {t("stickers.pack.notFound")}
        </Text>
      ) : (
        <Stack gap="3">
          <Stack direction="horizontal" align="center" gap="3">
            <PackThumb pack={pack} />
            <Stack grow gap="0.5">
              <Text variant="footnote" color="text.secondary">
                {pack.scope === "workspace" ? t("stickers.pack.workspace") : t("stickers.pack.personal", { name: owner?.displayName ?? pack.createdBy })}
              </Text>
              <Text variant="footnote" color="text.secondary">
                {t("stickers.pack.count", { count: pack.stickers.length })}
              </Text>
            </Stack>
            <Button
              label={v.added ? t("stickers.pack.remove") : t("stickers.pack.add")}
              variant={v.added ? "secondary" : "primary"}
              size="sm"
              icon={v.added ? "check" : "plus"}
              onPress={() => (v.added ? vm.removePack(pack.id) : vm.addPack(pack.id))}
              testID={`${testID}-toggle`}
            />
          </Stack>
          {pack.stickers.length === 0 ? (
            <Text color="text.secondary">{t("stickers.pack.empty")}</Text>
          ) : (
            <Stack>
              <MediaGrid
                sections={[{ key: pack.id, items: pack.stickers.map((s) => ({ key: s.id, src: s.url, alt: s.name, width: s.width, height: s.height })) }]}
                columns={4}
                cell="square"
                onSelect={() => {}}
                contextMenu={(key) => (v.canEdit ? [{ id: "delete", label: t("stickers.sticker.delete"), icon: "trash", destructive: true, onSelect: () => void vm.deleteSticker(key) }] : [])}
                testID={`${testID}-grid`}
              />
            </Stack>
          )}
          {v.canEdit && onAddSticker !== undefined ? (
            <Stack direction="horizontal">
              <Button label={t("stickers.pack.addSticker")} variant="ghost" size="sm" icon="plus" onPress={() => onAddSticker(pack.id)} testID={`${testID}-add-sticker`} />
            </Stack>
          ) : null}
        </Stack>
      )}
    </Sheet>
  );
}

// ---------- Manager: My stickers, workspace packs, settings ----------

export type StickerManagerPanelProps = {
  vm: StickersViewModel;
  state: StickersState;
  onCreateSticker?: () => void;
  testID: string;
};

const FILTERS: { key: GifContentFilter; label: "stickers.settings.rating.high" | "stickers.settings.rating.medium" | "stickers.settings.rating.low" | "stickers.settings.rating.off" }[] = [
  { key: "high", label: "stickers.settings.rating.high" },
  { key: "medium", label: "stickers.settings.rating.medium" },
  { key: "low", label: "stickers.settings.rating.low" },
  { key: "off", label: "stickers.settings.rating.off" },
];

/**
 * Settings → Stickers & GIFs: reorder/remove my packs, add workspace packs, and (admins) who may create stickers and the
 * GIF filter. A settings page, not a sheet: the picker's "Manage" opens this page.
 */
export function StickerManagerPanel({ vm, state, onCreateSticker, testID }: StickerManagerPanelProps) {
  const t = useT();
  const mine = Stickers.myPacks(state.packs, state.myPackIds);
  const others: BrowseEntry[] = state.browse.filter((b) => !b.added);
  const filter = FILTERS.find((f) => f.key === state.settings.gifContentFilter) ?? FILTERS[0]!;
  return (
    <Stack gap="6" testID={testID}>
      <SettingsSection title={t("stickers.manage.mine")} testID={`${testID}-mine`}>
        <Stack gap="1">
          {mine.length === 0 ? (
            <Text variant="footnote" color="text.tertiary">
              {t("stickers.manage.emptyMine")}
            </Text>
          ) : (
            mine.map((p, i) => (
              <Stack key={p.id} direction="horizontal" align="center" gap="1" testID={`${testID}-mine-${p.id}`}>
                <Pressable direction="horizontal" radius="md" onPress={() => vm.openPack(p.id)} accessibilityLabel={t("stickers.manage.open", { name: p.name })} testID={`${testID}-open-${p.id}`}>
                  <Stack direction="horizontal" align="center" gap="2" padding="1">
                    <PackThumb pack={p} />
                    <Stack gap="0.5">
                      <Text lines={1}>{p.name}</Text>
                      <Text variant="caption" color="text.tertiary">
                        {t("stickers.pack.count", { count: p.stickers.length })}
                      </Text>
                    </Stack>
                  </Stack>
                </Pressable>
                <Spacer />
                <IconButton icon="chevronUp" size="sm" disabled={i === 0} accessibilityLabel={t("stickers.manage.moveUp")} onPress={() => vm.movePack(p.id, i - 1)} testID={`${testID}-up-${p.id}`} />
                <IconButton icon="chevronDown" size="sm" disabled={i === mine.length - 1} accessibilityLabel={t("stickers.manage.moveDown")} onPress={() => vm.movePack(p.id, i + 1)} testID={`${testID}-down-${p.id}`} />
                <IconButton icon="close" size="sm" accessibilityLabel={t("stickers.manage.remove")} onPress={() => vm.removePack(p.id)} testID={`${testID}-remove-${p.id}`} />
              </Stack>
            ))
          )}
        </Stack>
        {onCreateSticker === undefined ? null : (
          <Stack direction="horizontal">
            <Button label={t("picker.stickers.create")} icon="plus" variant="secondary" size="sm" onPress={onCreateSticker} testID={`${testID}-create`} />
          </Stack>
        )}
      </SettingsSection>
      {others.length === 0 ? null : (
        <SettingsSection title={t("stickers.manage.browse")} testID={`${testID}-browse`}>
          <Stack gap="1">
            {others.map((b) => (
              <Stack key={b.pack.id} direction="horizontal" align="center" gap="2" testID={`${testID}-browse-${b.pack.id}`}>
                <Pressable direction="horizontal" radius="md" onPress={() => vm.openPack(b.pack.id)} accessibilityLabel={t("stickers.manage.open", { name: b.pack.name })} testID={`${testID}-browse-open-${b.pack.id}`}>
                  <Stack direction="horizontal" align="center" gap="2" padding="1">
                    <PackThumb pack={b.pack} />
                    <Stack gap="0.5">
                      <Text lines={1}>{b.pack.name}</Text>
                      <Text variant="caption" color="text.tertiary">
                        {b.pack.scope === "workspace" ? t("stickers.pack.workspace") : t("stickers.pack.count", { count: b.pack.stickers.length })}
                      </Text>
                    </Stack>
                  </Stack>
                </Pressable>
                <Spacer />
                <Button label={t("stickers.manage.add")} size="sm" variant="secondary" icon="plus" onPress={() => vm.addPack(b.pack.id)} testID={`${testID}-add-${b.pack.id}`} />
              </Stack>
            ))}
          </Stack>
        </SettingsSection>
      )}
      {!state.canManage ? null : (
        <SettingsSection title={t("stickers.manage.settings")} description={t("stickers.settings.guests")} testID={`${testID}-settings`}>
            <Stack direction="horizontal" align="center" gap="2">
              <Stack grow>
                <Text>{t("stickers.settings.creators")}</Text>
              </Stack>
              <Menu
                align="end"
                items={[
                  { id: "everyone", label: t("stickers.settings.creators.everyone"), onSelect: () => void vm.updateSettings({ stickerCreators: "everyone" }) },
                  { id: "admins", label: t("stickers.settings.creators.admins"), onSelect: () => void vm.updateSettings({ stickerCreators: "admins" }) },
                ]}
                testID={`${testID}-creators-menu`}
              >
                <Button
                  label={state.settings.stickerCreators === "everyone" ? t("stickers.settings.creators.everyone") : t("stickers.settings.creators.admins")}
                  size="sm"
                  variant="secondary"
                  onPress={() => {}}
                  testID={`${testID}-creators`}
                />
              </Menu>
            </Stack>
            <Toggle value={state.settings.gifsEnabled} onChange={(v) => void vm.updateSettings({ gifsEnabled: v })} label={t("stickers.settings.gifsEnabled")} testID={`${testID}-gifs`} />
            <Stack direction="horizontal" align="center" gap="2">
              <Stack grow>
                <Text>{t("stickers.settings.rating")}</Text>
              </Stack>
              <Menu
                align="end"
                items={FILTERS.map((f) => ({ id: f.key, label: t(f.label), onSelect: () => void vm.updateSettings({ gifContentFilter: f.key }) }))}
                testID={`${testID}-rating-menu`}
              >
                <Button label={t(filter.label)} size="sm" variant="secondary" onPress={() => {}} testID={`${testID}-rating`} />
              </Menu>
            </Stack>
        </SettingsSection>
      )}
    </Stack>
  );
}

/**
 * The workspace's sticker sheets (creator, pack) and their notice toast, rendered once by the app shell next
 * to the panes; screens open them through the view models (`creator.open`, `openPack`). The manager is a settings page (`StickerManagerPanel`).
 */
export function StickerSheets({ testID }: { testID: string }) {
  const t = useT();
  const tr = useTr();
  const s = useStickers();
  if (s === null) return null;
  return (
    <>
      <StickerPackSheet vm={s.vm} state={s.state} onAddSticker={s.state.canCreate ? (packId) => s.creator.open(undefined, packId) : undefined} testID={`${testID}-pack`} />
      <StickerCreatorSheet vm={s.creator} state={s.creatorState} testID={`${testID}-creator`} />
      <Toast
        open={s.state.notice !== undefined}
        onClose={() => s.vm.dismissNotice()}
        message={s.state.notice === undefined ? "" : tr(s.state.notice)}
        closeLabel={t("common.close")}
        durationMs={5000}
        testID={`${testID}-notice`}
      />
    </>
  );
}
